import { Link, Outlet, createRootRoute, useRouterState } from "@tanstack/react-router";
import { ShieldOffIcon, TriangleAlertIcon } from "lucide-react";
import { PAGES, routingRole, seesRoutingPages, writesRouting } from "@app/shared";
import type { RoutingRole } from "@app/shared";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  AppShell,
  AuthGate,
  Button,
  Toaster,
  useMe,
  useNavLinks,
} from "@axny/platform-ui";
import { cn } from "@/lib/utils";
import { SourceVersionWatch } from "@/features/routing/data";
import { AppUserSeen } from "@/features/routing/app-user";
import { IdleNotice, IdlePill } from "@/features/routing/idle-notice";
import { PreRouteMover } from "@/features/routing/pre-route-mover";
import { usePreRouteAlerts } from "@/features/routing/pre-route-page";

/**
 * App shell. Nothing here fetches report data — each route owns its queries, so a
 * slow table cannot delay the nav.
 *
 * `errorComponent` is the client-side counterpart to the Express error
 * middleware. KEEP IT: without it a render-time exception unmounts the whole app
 * and leaves a blank page that looks identical to an outage.
 */
export const Route = createRootRoute({
  component: RootLayout,
  errorComponent: RouteError,
  notFoundComponent: NotFound,
});

/**
 * `AuthGate` wraps the shell rather than sitting inside it, so an anonymous
 * visitor never sees a half-rendered app behind a redirect — and the error and
 * not-found components use the same shell, which also wants a signed-in user.
 */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <AuthGate>
      {/* The layout is the platform's `AppShell` — the sticky banner-and-header
          block, the page, the footer. It used to be written out here, and the
          copy in the xwatch app had drifted: no sticky block, so its STAGING
          banner scrolled away on exactly the long table where that matters. */}
      <PermittedShell>{children}</PermittedShell>
    </AuthGate>
  );
}

/**
 * Inside `AuthGate`, so the feature grants from `/api/me` are already there. Someone in the app's
 * group but in neither Role group has no tab (the platform shows a page only to its feature) and,
 * on this app's pages, the page below instead: nothing of them mounts, so nothing is read and Ask
 * AI stays hidden. The platform's own pages (My access) still open.
 */
function PermittedShell({ children }: { children: React.ReactNode }) {
  const me = useMe();
  const role = routingRole(me.data?.authenticated ? me.data.features : undefined);
  const path = useRouterState({ select: (s) => s.location.pathname });
  const ours = path === "/" || path.startsWith("/routing");
  const routing = seesRoutingPages(role);
  const writes = writesRouting(me.data?.authenticated ? me.data.features : undefined);
  const alerts = usePreRouteAlerts(routing);
  // The Pre-route tab says its alerts, as the spec's red badge (OUTSIDE-PLATFORM: P18 a nav entry's label is text only).
  const links = useNavLinks().map((l) => ({
    ...l,
    children: l.children?.map((c) =>
      c.to === PAGES["pre-route"].path && alerts ? { ...c, label: `${c.label} (${alerts})` } : c,
    ),
  }));
  // No `brand`: the gate and the shell show app.json's title (D163).
  // The Toaster: the small grey messages at the bottom (saved, approved) — platform-ui mounts none.
  return (
    <Toaster>
      <AppShell
        links={links}
        headerActions={
          <>
            {routing && alerts ? <PreRouteAlertBadge count={alerts} /> : null}
            {/* "Updates paused · 2:14 PM" while the page is idle; "Updating…" as it resumes. */}
            {ours && routing ? <IdlePill /> : null}
            <RoleChip role={role} />
          </>
        }
      >
        {ours && !routing ? <NoRoutingAccess /> : children}
        {/* The automatic move of pre-routes runs wherever a routing page is open (P17: not the server yet) —
            never in a Viewer's session: it writes, and every claim would be a 403. */}
        {ours && routing && writes ? <PreRouteMover /> : null}
        {/* New A2000 or Deposco rows: every routing page reads its data again (data.ts). */}
        {ours && routing ? <SourceVersionWatch /> : null}
        {/* Nobody on the page for 30 minutes: nothing refreshes, and a notice says so (live-updates.ts). */}
        {ours && routing ? <IdleNotice /> : null}
        {/* The person's own app-user record: whom Admin → Customer assignments → Add suggests (a Viewer's too). */}
        {ours && routing ? <AppUserSeen /> : null}
      </AppShell>
    </Toaster>
  );
}

/** The red badge of the Pre-route tab, on every page: its alerts (spec §3.12; in the app only, Q8). */
function PreRouteAlertBadge({ count }: { count: number }) {
  return (
    <Link
      // A code route (`pages.tsx`): its path and search are not in the router's types.
      to={PAGES["pre-route"].path as never}
      search={{ tab: "alerts" } as never}
      data-testid="pre-route-alerts"
      title="Pre-routes whose pickup is 2 business days away or less, or passed, with no pick ticket in the WMS"
      className="flex items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-xs font-medium whitespace-nowrap text-white hover:bg-red-700"
    >
      <TriangleAlertIcon className="size-3" />
      {count} pre-route alert{count === 1 ? "" : "s"}
    </Link>
  );
}

/**
 * The person's role, beside the account menu; a Viewer's says "View only". OUTSIDE-PLATFORM: P16 the
 * account menu has no line under the name for it (docs/platform-requests.md), so it sits in the header's actions.
 */
function RoleChip({ role }: { role: RoutingRole }) {
  return (
    <span
      data-testid="routing-role"
      title={
        role === "Viewer"
          ? "Read-only access: you can filter, sort and export, not change"
          : "Your role in Production & Routing"
      }
      className={cn(
        "rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap",
        role === "Manager" && "border-violet-300 bg-violet-50 text-violet-800",
        role === "Warehouse" && "border-teal-300 bg-teal-50 text-teal-800",
        role === "Viewer" && "border-slate-300 bg-slate-50 text-slate-700",
        role === "No access" && "text-muted-foreground border-dashed",
      )}
    >
      {role === "Viewer" ? "View only" : role}
    </span>
  );
}

/** In the app's group, in neither Role group: what to do about it. */
function NoRoutingAccess() {
  return (
    <div className="mx-auto max-w-lg py-16 text-center" data-testid="routing-no-access">
      <ShieldOffIcon className="text-muted-foreground mx-auto size-10" />
      <h1 className="mt-4 text-lg font-semibold">You don't have access to Production & Routing yet.</h1>
      <p className="text-muted-foreground mt-2 text-sm">
        Ask a manager to add you to the warehouse group. Once you are in it, sign in again (or use “Refresh sign-in” in
        the account menu) and the Worklist, Pre-route, Shipping, History and the System log appear here.
      </p>
    </div>
  );
}

function RootLayout() {
  return (
    <Shell>
      <Outlet />
    </Shell>
  );
}

/**
 * A bug, not a data failure — those are handled inside the table and the chart
 * with the server's own message.
 *
 * So the message is generic and the error itself is not rendered: its text is as
 * likely to be "Cannot read properties of undefined" as anything actionable.
 * Reload is offered because it genuinely fixes the common cause, a stale bundle.
 */
function RouteError() {
  return (
    <Shell>
      <Alert variant="destructive">
        <AlertTitle>Something went wrong on this page</AlertTitle>
        <AlertDescription>
          <p>Reloading usually fixes it. If it keeps happening, send us the build shown below.</p>
          <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
            Reload
          </Button>
        </AlertDescription>
      </Alert>
    </Shell>
  );
}

function NotFound() {
  return (
    <Shell>
      <h1 className="text-lg font-semibold">Page not found</h1>
      <p className="text-muted-foreground mt-2 text-sm">That link does not match any report in this app.</p>
    </Shell>
  );
}
